import { useCallback, useMemo, useState } from 'react';
import { Row } from './Datepicker.styles';
import { Day } from './Day';
import { DayOfWeek } from './DayOfWeek';
import {
  createNewDateRange,
  DateRange,
  getDayName,
  getWeekDates,
  isDateAfter,
  isDateBefore,
  isDateEnd,
  isDateInRange,
  isDateSelected,
  isDateStart,
  isSameDate,
  DAYS_OF_WEEK
} from './utilities';

export interface WeekProps {
  weekDate: Date;
  weekStartDay?: number;
  rangeEnabled?: boolean;
  selected?: DateRange;
  disableDatesBefore?: Date;
  disableDatesAfter?: Date;
  disableSpecificDates?: Date[];
  onChange?: (date: DateRange) => void;
}

export const Week = ({
  weekDate,
  weekStartDay = 0,
  rangeEnabled,
  selected,
  disableDatesBefore,
  disableDatesAfter,
  disableSpecificDates,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange = () => {}
}: WeekProps) => {
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  const weekDates = useMemo(() => getWeekDates(weekDate, weekStartDay), [weekDate, weekStartDay]);

  const daysOfWeek = useMemo(() => {
    const orderedDays = [];
    for (let i = 0; i < 7; i++) {
      const dayIndex = (weekStartDay + i) % 7;
      orderedDays.push(DAYS_OF_WEEK[dayIndex]);
    }
    return orderedDays.map(dow => <DayOfWeek key={dow} label={getDayName(dow)} />);
  }, [weekStartDay]);

  const dayClickHandler = useCallback(
    (selectedDate: Date) => {
      onChange(createNewDateRange(rangeEnabled ? selected : undefined, selectedDate));
    },
    [rangeEnabled, selected, onChange]
  );

  const hoverHandler = useCallback((date: Date) => {
    setHoverDate(date);
  }, []);

  const renderDay = (day: Date, dayIndex: number) => {
    const disabled =
      (disableDatesBefore && isDateBefore(day, disableDatesBefore)) ||
      (disableDatesAfter && isDateAfter(day, disableDatesAfter)) ||
      disableSpecificDates?.some(date => isSameDate(date, day));

    const firstInRange = rangeEnabled && selected && isDateStart(day, selected);
    const lastInRange =
      rangeEnabled &&
      selected &&
      ((!isSameDate(selected.start, selected.end) && isDateEnd(day, selected)) ||
        (hoverDate !== null &&
          !firstInRange &&
          isSameDate(day, hoverDate) &&
          isSameDate(selected.start, selected.end) &&
          isDateAfter(hoverDate, selected.start)));

    const inHoveringRange =
      rangeEnabled &&
      selected &&
      hoverDate !== null &&
      isSameDate(selected.start, selected.end) &&
      day > selected.start &&
      day < hoverDate;

    const isHoverDate =
      rangeEnabled &&
      hoverDate !== null &&
      selected &&
      isDateAfter(day, selected.start) &&
      !!isSameDate(selected.start, selected.end) &&
      isSameDate(day, hoverDate);

    const unavailable = false;

    const lastOfWeek = weekDates[weekDates.length - 1];

    return (
      <Day
        key={dayIndex}
        day={day}
        selected={selected && isDateSelected(day, selected)}
        inRange={rangeEnabled && selected && isDateInRange(day, selected)}
        inHoveringRange={inHoveringRange}
        isHoverDate={isHoverDate}
        firstInRange={firstInRange}
        lastInRange={lastInRange}
        lastOfMonth={lastOfWeek}
        disabled={disabled}
        unavailable={unavailable}
        onClick={dayClickHandler}
        onHover={hoverHandler}
        data-analytics-id="pp_datepicker_select_day"
      />
    );
  };

  return (
    <>
      <Row>{daysOfWeek}</Row>
      <Row>{weekDates.map(renderDay)}</Row>
    </>
  );
};
